import { createContext, useCallback, useEffect, useState } from 'react'

import { sdk } from '../../services/sdk'

interface ProvidersDataContext {
  loading: boolean
  checkboxRisksFilter: boolean
  activeProviders: number
  inactiveProviders: number
  providersCount: number
  riskProviders: number
  providersWithInvoices: number
  certificateTypes: string[]
  selectedCertificateType?: string
  providersWithCertificates: { type: string; count: number }[]
  actions: {
    setCheckboxRisksFilter: (value: boolean) => void
    getProvidersStatistics: () => Promise<void>
    setSelectedCertificateType: (value: string | undefined) => void
    refreshCertificateTypes: () => Promise<void>
  }
}

interface ProvidersProviderProps {
  children: React.ReactNode
}

export const ProvidersContext = createContext({} as ProvidersDataContext)

export function ProvidersProvider(props: ProvidersProviderProps) {
  const [loading] = useState(false)
  const [checkboxRisksFilter, setCheckboxRisksFilter] = useState(false)
  const [certificateTypes, setCertificateTypes] = useState<string[]>([])
  const [selectedCertificateType, setSelectedCertificateType] = useState<
    string | undefined
  >(undefined)

  const [activeProviders, setActiveProviders] = useState(0)
  const [inactiveProviders, setInactiveProviders] = useState(0)
  const [providersWithInvoices, setProvidersWithInvoices] = useState(0)
  const [providersCount, setProvidersCount] = useState(0)
  const [riskProviders, setRiskProviders] = useState(0)
  const [providersWithCertificates, setProvidersWithCertificates] = useState<
    { type: string; count: number }[]
  >([])

  const getProvidersStatistics = async () => {
    try {
      const _response = await sdk.traceability.companiesStatistics()
      setActiveProviders(_response.providerCompaniesActiveQuantitySum.value)
      setInactiveProviders(_response.providerCompaniesInactiveQuantitySum.value)
      setProvidersCount(_response.providerCompaniesQuantitySum.value)
      setRiskProviders(
        _response.providerCompaniesWithSocialEnvironmentalRiskQuantitySum.value,
      )
      setProvidersWithInvoices(
        _response.providerCompaniesWithInvoicesQuantitySum.value,
      )
      setProvidersWithCertificates(
        (_response as any).providerCompaniesWithCertificates as {
          type: string
          count: number
        }[],
      )
    } catch (error) {
      console.error(error)
    }
  }

  const refreshCertificateTypes = useCallback(async () => {
    try {
      const types = await sdk.certificate.getTypes()
      setCertificateTypes(types)
    } catch (error) {
      console.error(error)
    }
  }, [])

  useEffect(() => {
    refreshCertificateTypes()
  }, [refreshCertificateTypes])

  return (
    <ProvidersContext.Provider
      value={{
        loading,
        checkboxRisksFilter,
        activeProviders,
        inactiveProviders,
        providersCount,
        riskProviders,
        providersWithInvoices,
        certificateTypes,
        selectedCertificateType,
        providersWithCertificates,
        actions: {
          setCheckboxRisksFilter,
          getProvidersStatistics,
          setSelectedCertificateType,
          refreshCertificateTypes,
        },
      }}
    >
      {props.children}
    </ProvidersContext.Provider>
  )
}
